import {
	IonButton,
	IonContent,
	IonFooter,
	IonHeader,
	IonPage,
	IonSkeletonText,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";
import {
	BarcodeFormat,
	DecodeHintType,
	Exception,
	Result,
} from "@zxing/library";
import { useCallback, useEffect, useRef, useState } from "react";
import { delay, get, isEmpty, isFunction, throttle } from "lodash";
import { BrowserMultiFormatOneDReader, IScannerControls } from "@zxing/browser";
import { postDebugPhoto } from "../../api";
import useAuth from "../../context/auth.context";
import { EMPTY, catchError, from, switchMap } from "rxjs";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { DEFAULT_TOAST_DURATION } from '../../constants';

const formats = [BarcodeFormat.CODE_39];
const hints = new Map();
hints.set(DecodeHintType.ALLOWED_LENGTHS, [8]);
hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);

const centeredStyle = {
	display: "block",
	marginLeft: "auto",
	marginRight: "auto",
	paddingLeft: 0,
	paddingRight: 0,
};

function isValidBarcode(code: string | undefined): boolean {
	//@ts-ignore
	return !isEmpty(code) && code.length === 8;
}

enum DebugState {
	START = "START",
	SEARCHING = "SEARCHING",
	FOUND_VALID_CODE = "FOUND_VALID_CODE",
	FOUND_INVALID_CODE = "FOUND_INVALID_CODE",
	CODE_NOT_FOUND = "CODE_NOT_FOUND",
	NOT_AUTHORIZED = "NOT_AUTHORIZED",
	CONNECTION_ERROR = "CONNECTION_ERROR",
}

export function DebugPage() {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const readerRef = useRef(new BrowserMultiFormatOneDReader(hints));
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [barcode, setBarcode] = useState<string | undefined>(undefined);
	const controlsRef = useRef<IScannerControls | null>(null);
	const { acquireToken, username, validateUser } = useAuth();
	const [countdownTimer, setCountdownTimer] = useState<number | undefined>(
		undefined
	);
	const { t } = useTranslation();
	const history = useHistory();
	const [roleIssueReported, setRoleIssueReported] = useState<boolean>(false);

	const appInsights = useAppInsightsContext();
	const trackRoleIssue = useTrackEvent(appInsights, 'RoleIssueReported', {});

	useEffect(() => {
		validateUser(() => history.push('/signin'));
	}, [])

	const translatedDebugState = {
		START: "",
		SEARCHING: t("debug.searching"),
		FOUND_VALID_CODE: t("debug.foundValidCode"),
		FOUND_INVALID_CODE: t("debug.foundInvalidCode"),
		CODE_NOT_FOUND: t("debug.codeNotFound"),
		NOT_AUTHORIZED: t("debug.notAuthorizedError"),
		CONNECTION_ERROR: t("debug.connectionError"),
	};

	const [debugState, setDebugState] = useState<DebugState>(DebugState.START);

	const throttledCallback = useCallback(
		throttle(onError, 10000, {
			leading: false,
			trailing: true,
		}),
		[]
	);

	function reportStatus(response: any) {
		if (response.status === 403) {
			console.log("Not authorized");
			setDebugState(DebugState.NOT_AUTHORIZED);
		} else if (response.status !== 200) {
			setDebugState(DebugState.CONNECTION_ERROR);
		}
	}

	function startCountdown() {
		// create countdown
		let count = 10;
		const interval = setInterval(() => {
			if (count > 0) {
				setCountdownTimer(count);
				count--;
			} else {
				setCountdownTimer(undefined);
				clearInterval(interval);
			}
		}, 1000);
	}

	function onError(
		error: Exception,
		captureSessionUuid: string,
		videoElement: any
	) {
		const canvas = canvasRef.current;
		if (canvas && username) {
			const context = canvas.getContext("2d");

			//get width height ratio
			canvas.height = videoElement.videoHeight;
			canvas.width =
				videoElement.videoHeight *
				(videoElement.videoWidth / videoElement.videoHeight);

			/** @ts-ignore */
			context?.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
			acquireToken()
				.pipe(
					catchError((error) => {
						console.error(error);
						startCountdown();
						return EMPTY;
					}),
					switchMap((accessToken) => {
						const imageData = canvas
							.toDataURL()
							.split(";base64,")[1];
						const date = new Date();
						const timestamp = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

						return postDebugPhoto(
							imageData,
							`${captureSessionUuid}_${timestamp}`,
							accessToken,
							username,
							false
						);
					})
				)
				.subscribe((response) => {
					reportStatus(response);
					startCountdown();
				});

			// const imageData = canvas.toDataURL().split(';base64,')[1];
			// const date = new Date();
			// const timestamp = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
			// postDebugPhoto(imageData, `${captureSessionUuid}_${timestamp}`, "", username, false)
			// 	.pipe(catchError((error) => {
			// 		console.error(error);
			// 		startCountdown();
			// 		return EMPTY;
			// 	}))
			// 	.subscribe(response => {
			// 		reportStatus(response);
			// 		startCountdown();
			// 		delay(() => {
			// 			setDebugState(DebugState.SEARCHING);
			// 		}, 1000);
			// 	});
		}
	}

	function onSuccess(
		captureSessionUuid: string,
		videoElement: any,
		barcode?: string
	) {
		throttledCallback.cancel();
		const canvas = canvasRef.current;
		if (canvas && username) {
			const context = canvas.getContext("2d");
			/** @ts-ignore */
			context?.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
			acquireToken()
				.pipe(
					catchError((error) => {
						console.error(error);
						return EMPTY;
					}),
					switchMap((accessToken) => {
						const imageData = canvas
							.toDataURL()
							.split(";base64,")[1];
						const date = new Date();
						const timestamp = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

						if (isValidBarcode(barcode)) {
							setDebugState(DebugState.FOUND_VALID_CODE);
							return postDebugPhoto(
								imageData,
								`${captureSessionUuid}_${timestamp}`,
								accessToken,
								username,
								true
							);
						} else {
							setDebugState(DebugState.FOUND_INVALID_CODE);
							return postDebugPhoto(
								imageData,
								`${captureSessionUuid}_${timestamp}`,
								accessToken,
								username,
								false
							);
						}
					})
				)
				.subscribe(reportStatus);

			// const imageData = canvas.toDataURL().split(';base64,')[1];
			// const date = new Date();
			// const timestamp = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

			// if (isValidBarcode(barcode)) {
			// 	setDebugState(DebugState.FOUND_VALID_CODE);
			// 	postDebugPhoto(imageData, `${captureSessionUuid}_${timestamp}`, "", username, true)
			// 		.subscribe(reportStatus);
			// } else {
			// 	setDebugState(DebugState.FOUND_INVALID_CODE);
			// 	postDebugPhoto(imageData, `${captureSessionUuid}_${timestamp}`, "", username, false)
			// 		.subscribe(reportStatus);
			// }
		}
	}

	async function startCapture() {
		setDebugState(DebugState.SEARCHING);
		startCountdown();
		// get hours, minutes, seconds of current time
		const captureSessionUuid = nanoid(10);

		if (!videoRef.current) {
			return;
		}

		await readerRef.current.decodeFromConstraints(
			{
				audio: false,
				video: {
					facingMode: "environment",
				},
			},
			videoRef.current,
			function (
				result: Result | undefined,
				error: Exception | undefined,
				controls: IScannerControls
			) {
				if (controlsRef.current === null) {
					controlsRef.current = controls;
				}
				if (
					error &&
					onError &&
					isFunction(onError) &&
					videoRef.current
				) {
					throttledCallback(
						error,
						captureSessionUuid,
						videoRef.current
					);
				}
				if (typeof result !== "undefined") {
					onSuccess(
						captureSessionUuid,
						videoRef.current,
						result.getText()
					);
					setBarcode(result.getText());
					controls.stop();
				}
			}
		);
	}

	function getTextColor() {
		switch (debugState) {
			case DebugState.CONNECTION_ERROR:
			case DebugState.NOT_AUTHORIZED:
			case DebugState.FOUND_INVALID_CODE:
			case DebugState.CODE_NOT_FOUND:
				return "red";
			case DebugState.FOUND_VALID_CODE:
				return "green";
			default:
				return "inherit";
		}
	}

	function onRestart() {
		controlsRef.current?.stop();
		throttledCallback.cancel();
		setDebugState(DebugState.START);
		setBarcode(undefined);
	}

	function getDebugStateString() {
		return translatedDebugState[debugState];
	}

	function onReportRoleIssue() {
		acquireToken()
			.pipe(
				switchMap((accessToken) => from(
					fetch(
						"https://graph.microsoft.com/v1.0/me/memberOf",
						{
							method: "GET",
							headers: {
								Authorization: `Bearer ${accessToken}`,
								Accept: "application/json",
								"Content-Type": "application/json",
							},
						}
					)
				)),
				switchMap((response) => {
					return response.json();
				})
			)
			.subscribe((response) => {
				const roleIds = get(response, 'value', []).map((role: any) => role.id);
				trackRoleIssue({ roleIds });
				setRoleIssueReported(true);
			});
	}

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonTitle>{t("debug.title")}</IonTitle>
				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen>
				{debugState !== DebugState.FOUND_VALID_CODE && (
					<video
						ref={videoRef}
						style={{
							marginBottom: "2rem",
						}}
					/>
				)}
				<canvas
					ref={canvasRef}
					style={{
						...centeredStyle,
						marginBottom: "2rem",
						display:
							debugState === DebugState.FOUND_VALID_CODE ||
								debugState === DebugState.FOUND_INVALID_CODE
								? "block"
								: "none",
						width: "100%",
					}}
				/>
				{(debugState === DebugState.SEARCHING ||
					debugState === DebugState.CODE_NOT_FOUND) && (
						<IonSkeletonText
							animated={true}
							style={{ "--background": "white", width: "100%" }}
						/>
					)}
				<div
					style={{
						...centeredStyle,
						color: getTextColor(),
						fontSize: "1.5rem",
						textAlign: "center",
					}}
				>
					{getDebugStateString()}{" "}
					{debugState === DebugState.SEARCHING && countdownTimer}{" "}
					{barcode}
				</div>
				{(debugState === DebugState.SEARCHING ||
					debugState === DebugState.CODE_NOT_FOUND) && (
						<IonSkeletonText
							animated={true}
							style={{ "--background": "white", width: "100%" }}
						/>
					)}
				<IonToast
					isOpen={roleIssueReported}
					message={t("debug.roleIssueReportedMessage")}
					onDidDismiss={() => setRoleIssueReported(false)}
					duration={DEFAULT_TOAST_DURATION}
				></IonToast>
			</IonContent>
			<IonFooter>
				<IonToolbar>
					{debugState === DebugState.START && (
						<IonButton
							color="primary"
							expand="block"
							onClick={startCapture}
							style={{ ...centeredStyle }}
						>
							{t("general.start")}
						</IonButton>
					)}
					{debugState === DebugState.START && (
						<IonButton
							color="primary"
							expand="block"
							onClick={onReportRoleIssue}
							style={{ ...centeredStyle }}
						>
							{t("debug.reportRoleIssue")}
						</IonButton>
					)}
					{debugState !== DebugState.START && (
						<IonButton
							color="primary"
							expand="block"
							onClick={onRestart}
							style={{ ...centeredStyle }}
						>
							{t("general.back")}
						</IonButton>
					)}
				</IonToolbar>
			</IonFooter>
		</IonPage>
	);
}
